/**
 * Bulk Upload — Validation Engine
 *
 * Validates each parsed spreadsheet row against an UploadSchema.
 * Handles type coercion, required checks, enum validation, constraints,
 * relation placeholder insertion, cross-field rules, and context defaults.
 */

import {
	ColumnMapping,
	ContextDefault,
	FieldError,
	FieldWarning,
	UploadFieldDef,
	UploadSchema,
	ValidationResult,
	RowStatus,
} from './types';
import { CsvRow } from '../../../../@types';

// ---------------------------------------------------------------------------
// Type Coercion Helpers
// ---------------------------------------------------------------------------

/** Try to parse a value as a number. Returns NaN on failure. */
function coerceNumber(raw: unknown): number {
	if (raw === null || raw === undefined || raw === '') return NaN;
	if (typeof raw === 'number') return raw;
	const str = String(raw).replace(/,/g, '').trim();
	if (str === '') return NaN;
	return Number(str);
}

/** Try to parse a value as a Date. Returns null on failure. */
function coerceDate(raw: unknown): Date | null {
	if (raw === null || raw === undefined || raw === '') return null;
	if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
	const str = String(raw).trim();
	if (str === '') return null;

	// Excel serial number (days since 1899-12-30)
	const serial = Number(str);
	if (!isNaN(serial) && serial > 1 && serial < 200000) {
		const epoch = new Date(Date.UTC(1899, 11, 30));
		epoch.setUTCDate(epoch.getUTCDate() + serial);
		return epoch;
	}

	const parsed = new Date(str);
	return isNaN(parsed.getTime()) ? null : parsed;
}

/** Try to parse a value as a boolean. Returns null on failure. */
function coerceBoolean(raw: unknown): boolean | null {
	if (typeof raw === 'boolean') return raw;
	if (raw === null || raw === undefined || raw === '') return null;
	const str = String(raw).trim().toLowerCase();
	if (['true', '1', 'yes', 'y', 'x'].includes(str)) return true;
	if (['false', '0', 'no', 'n', ''].includes(str)) return false;
	return null;
}

/** Normalize an enum value to match the allowed list (case-insensitive, underscore/space tolerant). */
function matchEnum(raw: string, allowed: readonly string[]): string | null {
	const norm = raw
		.trim()
		.toUpperCase()
		.replace(/[\s-]+/g, '_');
	// Direct match
	const exact = allowed.find((v) => v.toUpperCase() === norm);
	if (exact) return exact;
	// Partial: allow dropping underscores
	const stripped = norm.replace(/_/g, '');
	return allowed.find((v) => v.toUpperCase().replace(/_/g, '') === stripped) ?? null;
}

// ---------------------------------------------------------------------------
// Single-Cell Validation
// ---------------------------------------------------------------------------

interface CellResult {
	value: unknown;
	error: FieldError | null;
	warning: FieldWarning | null;
}

function validateCell(field: UploadFieldDef, raw: unknown): CellResult {
	const isEmpty = raw === null || raw === undefined || String(raw).trim() === '';

	// Required check
	if (isEmpty) {
		if (field.required && field.defaultValue === undefined) {
			return {
				value: undefined,
				error: { field: field.name, message: `${field.label} is required`, value: raw },
				warning: null,
			};
		}
		// Use default or leave undefined
		return { value: field.defaultValue ?? undefined, error: null, warning: null };
	}

	// Apply transform first
	let transformed: unknown = raw;
	if (field.transform) {
		try {
			transformed = field.transform(String(raw));
		} catch {
			return {
				value: raw,
				error: { field: field.name, message: `Transform failed for ${field.label}`, value: raw },
				warning: null,
			};
		}
	}

	const strVal = String(transformed).trim();

	switch (field.dataType) {
		case 'string': {
			if (field.constraints?.minLength && strVal.length < field.constraints.minLength) {
				return {
					value: strVal,
					error: {
						field: field.name,
						message: `${field.label} must be at least ${field.constraints.minLength} characters`,
						value: strVal,
					},
					warning: null,
				};
			}
			if (field.constraints?.maxLength && strVal.length > field.constraints.maxLength) {
				return {
					value: strVal,
					error: {
						field: field.name,
						message: `${field.label} must be at most ${field.constraints.maxLength} characters`,
						value: strVal,
					},
					warning: null,
				};
			}
			if (field.constraints?.pattern && !field.constraints.pattern.test(strVal)) {
				return {
					value: strVal,
					error: {
						field: field.name,
						message: `${field.label} does not match the required format`,
						value: strVal,
					},
					warning: null,
				};
			}
			return { value: strVal, error: null, warning: null };
		}

		case 'number': {
			const num = coerceNumber(transformed);
			if (isNaN(num)) {
				return {
					value: raw,
					error: { field: field.name, message: `${field.label} must be a number`, value: raw },
					warning: null,
				};
			}
			if (field.constraints?.min !== undefined && num < field.constraints.min) {
				return {
					value: num,
					error: {
						field: field.name,
						message: `${field.label} must be ≥ ${field.constraints.min}`,
						value: num,
					},
					warning: null,
				};
			}
			if (field.constraints?.max !== undefined && num > field.constraints.max) {
				return {
					value: num,
					error: {
						field: field.name,
						message: `${field.label} must be ≤ ${field.constraints.max}`,
						value: num,
					},
					warning: null,
				};
			}
			return { value: num, error: null, warning: null };
		}

		case 'date': {
			const d = coerceDate(transformed);
			if (!d) {
				return {
					value: raw,
					error: {
						field: field.name,
						message: `${field.label} must be a valid date`,
						value: raw,
					},
					warning: null,
				};
			}
			return { value: d.toISOString().split('T')[0], error: null, warning: null };
		}

		case 'boolean': {
			const b = coerceBoolean(transformed);
			if (b === null) {
				return {
					value: raw,
					error: {
						field: field.name,
						message: `${field.label} must be a boolean (yes/no, true/false, 1/0)`,
						value: raw,
					},
					warning: null,
				};
			}
			return { value: b, error: null, warning: null };
		}

		case 'enum': {
			if (!field.enumValues || field.enumValues.length === 0) {
				return { value: strVal, error: null, warning: null };
			}
			const matched = matchEnum(strVal, field.enumValues);
			if (!matched) {
				return {
					value: raw,
					error: {
						field: field.name,
						message: `${field.label} must be one of: ${field.enumValues.join(', ')}`,
						value: raw,
					},
					warning: null,
				};
			}
			if (matched !== strVal) {
				return {
					value: matched,
					error: null,
					warning: {
						field: field.name,
						message: `Normalized "${strVal}" → "${matched}"`,
						value: matched,
					},
				};
			}
			return { value: matched, error: null, warning: null };
		}

		case 'relation': {
			// Relations are validated separately in the resolution step.
			// Here we just pass through the raw value as a placeholder.
			return {
				value: `__unresolved:${strVal}`,
				error: null,
				warning: null,
			};
		}

		default:
			return { value: strVal, error: null, warning: null };
	}
}

// ---------------------------------------------------------------------------
// Row Extraction
// ---------------------------------------------------------------------------

/**
 * Extract the raw value for a field from a spreadsheet row using the column mapping.
 */
function extractRawValue(
	row: CsvRow,
	field: UploadFieldDef,
	mappings: ColumnMapping[],
	contextDefaults: ContextDefault[]
): unknown {
	const mapping = mappings.find((m) => m.targetField === field.name);

	// Check mapped column first
	if (mapping?.sourceColumn) {
		const val = row[mapping.sourceColumn];
		if (val !== null && val !== undefined && String(val).trim() !== '') {
			return val;
		}
	}

	// Fall back to context defaults (if user confirmed them)
	const contextDef = contextDefaults.find((cd) => cd.targetField === field.name && cd.confirmed);
	if (contextDef) {
		// For relations, use the resolved ID if available
		if (field.dataType === 'relation' && contextDef.relation?.resolvedId) {
			return contextDef.relation.resolvedId;
		}
		return contextDef.resolvedValue ?? contextDef.rawValue;
	}

	return undefined;
}

// ---------------------------------------------------------------------------
// Full Validation Pipeline
// ---------------------------------------------------------------------------

export interface ValidateOptions {
	/** Column mappings from step 3 */
	mappings: ColumnMapping[];
	/** Context defaults from parsed context rows */
	contextDefaults?: ContextDefault[];
	/** Skip rows that are completely empty */
	skipEmptyRows?: boolean;
}

/**
 * Validate all parsed data rows against the schema.
 * Returns one ValidationResult per row.
 *
 * Note: Relation fields produce placeholder values (__unresolved:...) that will
 * be resolved in Step 4 (Resolve Relations) using resolve.ts.
 */
export function validateRows(
	schema: UploadSchema,
	rows: CsvRow[],
	options: ValidateOptions
): ValidationResult[] {
	const { mappings, contextDefaults = [], skipEmptyRows = true } = options;
	const results: ValidationResult[] = [];

	for (let i = 0; i < rows.length; i++) {
		const row = rows[i];

		// Check if row is entirely empty
		if (skipEmptyRows) {
			const hasAnyValue = Object.values(row).some(
				(v) => v !== null && v !== undefined && String(v).trim() !== ''
			);
			if (!hasAnyValue) {
				results.push({
					rowIndex: i,
					status: 'skipped',
					errors: [],
					warnings: [],
					resolvedRow: {},
				});
				continue;
			}
		}

		const errors: FieldError[] = [];
		const warnings: FieldWarning[] = [];
		const resolvedRow: Record<string, unknown> = {};

		// Validate each field
		for (const field of schema.fields) {
			const raw = extractRawValue(row, field, mappings, contextDefaults);

			// If a context default already resolved a relation to an ID, skip cell validation
			if (field.dataType === 'relation' && typeof raw === 'number' && raw > 0) {
				resolvedRow[field.name] = raw;
				continue;
			}

			const cellResult = validateCell(field, raw);
			if (cellResult.error) errors.push(cellResult.error);
			if (cellResult.warning) warnings.push(cellResult.warning);
			if (cellResult.value !== undefined) {
				resolvedRow[field.name] = cellResult.value;
			}
		}

		// Cross-field validation
		if (schema.crossFieldValidation) {
			const crossMsg = schema.crossFieldValidation(resolvedRow);
			if (crossMsg) {
				errors.push({
					field: '__cross',
					message: crossMsg,
					value: null,
				});
			}
		}

		const status: RowStatus =
			errors.length > 0 ? 'error' : warnings.length > 0 ? 'warning' : 'valid';

		results.push({ rowIndex: i, status, errors, warnings, resolvedRow });
	}

	// Within-batch duplicate detection (e.g. two rows with the same contract
	// number). The first occurrence is left alone; later occurrences are
	// flagged as errors so the user has to dedupe before submit.
	if (schema.duplicateKeyFields && schema.duplicateKeyFields.length > 0) {
		const seen = new Map<string, number>();
		for (const result of results) {
			if (result.status === 'skipped') continue;
			const keyParts: string[] = [];
			let keyComplete = true;
			for (const fieldName of schema.duplicateKeyFields) {
				const raw = result.resolvedRow[fieldName];
				if (raw === undefined || raw === null || String(raw).trim() === '') {
					keyComplete = false;
					break;
				}
				keyParts.push(String(raw).trim().toLowerCase());
			}
			if (!keyComplete) continue;
			const key = keyParts.join('\u0001');
			const firstIndex = seen.get(key);
			if (firstIndex === undefined) {
				seen.set(key, result.rowIndex);
				continue;
			}
			const dupFields = schema.duplicateKeyFields.join(' + ');
			const dupValue = schema.duplicateKeyFields
				.map((f) => String(result.resolvedRow[f]))
				.join(' / ');
			result.errors.push({
				field: schema.duplicateKeyFields[0],
				message: `Duplicate of row ${firstIndex + 1} (${dupFields}: ${dupValue})`,
				value: dupValue,
			});
			result.status = 'error';
		}
	}

	return results;
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export interface ValidationStats {
	total: number;
	valid: number;
	warning: number;
	error: number;
	skipped: number;
	/** Unique unresolved relation values across all rows */
	unresolvedRelations: number;
}

export function getValidationStats(results: ValidationResult[]): ValidationStats {
	let valid = 0;
	let warning = 0;
	let error = 0;
	let skipped = 0;
	const unresolvedSet = new Set<string>();

	for (const r of results) {
		switch (r.status) {
			case 'valid':
				valid++;
				break;
			case 'warning':
				warning++;
				break;
			case 'error':
				error++;
				break;
			case 'skipped':
				skipped++;
				break;
		}

		// Count unresolved relations
		for (const val of Object.values(r.resolvedRow)) {
			if (typeof val === 'string' && val.startsWith('__unresolved:')) {
				unresolvedSet.add(val);
			}
		}
	}

	return {
		total: results.length,
		valid,
		warning,
		error,
		skipped,
		unresolvedRelations: unresolvedSet.size,
	};
}

/**
 * Returns only the rows that are valid (or warning-only) for submission.
 * Strips relation placeholders — caller must resolve them first.
 * When mappings and schema are provided, strips fields that:
 *   - have no column mapping AND no context default
 *   - AND have a defaultValue (i.e., they are optional with frontend defaults)
 * This ensures the backend uses its own defaults for unmapped optional fields
 * like status, rather than the frontend injecting a value the user never chose.
 */
export function getSubmittableRows(
	results: ValidationResult[],
	options?: {
		mappings?: ColumnMapping[];
		contextDefaults?: ContextDefault[];
		schema?: UploadSchema;
	}
): Array<{ rowIndex: number; data: Record<string, unknown> }> {
	// Build the set of field names the user explicitly provided values for
	const mappedFields = new Set<string>();
	if (options?.mappings) {
		for (const m of options.mappings) {
			if (m.targetField) mappedFields.add(m.targetField);
		}
	}
	if (options?.contextDefaults) {
		for (const cd of options.contextDefaults) {
			if (cd.confirmed) mappedFields.add(cd.targetField);
		}
	}

	// Fields that are optional+defaulted AND not mapped should be omitted from payload
	const fieldsToOmit = new Set<string>();
	if (options?.schema && mappedFields.size > 0) {
		for (const field of options.schema.fields) {
			if (field.defaultValue !== undefined && !field.required && !mappedFields.has(field.name)) {
				fieldsToOmit.add(field.name);
			}
		}
	}

	return results
		.filter((r) => r.status === 'valid' || r.status === 'warning')
		.map((r) => ({
			rowIndex: r.rowIndex,
			data: Object.fromEntries(
				Object.entries(r.resolvedRow).filter(
					([k, v]) =>
						!(typeof v === 'string' && v.startsWith('__unresolved:')) && !fieldsToOmit.has(k)
				)
			),
		}));
}

// ---------------------------------------------------------------------------
// Single-Row Revalidation (for inline editing at Step 5)
// ---------------------------------------------------------------------------

/**
 * Re-validate a single row from its resolvedRow data.
 * Used when the user edits a cell inline during the Validate & Preview step.
 * Returns an updated ValidationResult with fresh errors/warnings/status.
 */
export function revalidateRow(schema: UploadSchema, result: ValidationResult): ValidationResult {
	const errors: FieldError[] = [];
	const warnings: FieldWarning[] = [];
	const resolvedRow: Record<string, unknown> = {};

	for (const field of schema.fields) {
		const raw = result.resolvedRow[field.name];

		// Skip relation fields that are already resolved to numeric IDs
		if (field.dataType === 'relation' && typeof raw === 'number' && raw > 0) {
			resolvedRow[field.name] = raw;
			continue;
		}

		// Skip resolved relation placeholders — keep them as-is
		if (typeof raw === 'string' && raw.startsWith('__unresolved:')) {
			resolvedRow[field.name] = raw;
			continue;
		}

		const cellResult = validateCell(field, raw);
		if (cellResult.error) errors.push(cellResult.error);
		if (cellResult.warning) warnings.push(cellResult.warning);
		if (cellResult.value !== undefined) {
			resolvedRow[field.name] = cellResult.value;
		}
	}

	// Cross-field validation
	if (schema.crossFieldValidation) {
		const crossMsg = schema.crossFieldValidation(resolvedRow);
		if (crossMsg) {
			errors.push({ field: '__cross', message: crossMsg, value: null });
		}
	}

	const status: RowStatus = errors.length > 0 ? 'error' : warnings.length > 0 ? 'warning' : 'valid';

	return { rowIndex: result.rowIndex, status, errors, warnings, resolvedRow };
}
